import type { ProcessedBreach } from '../types';
import { Shield, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';

interface BreachTableProps {
  breaches: ProcessedBreach[];
}

const severityConfig = {
  critical: {
    icon: AlertCircle,
    bg: 'bg-signal-red/10',
    text: 'text-signal-red',
    border: 'border-signal-red/30',
  },
  high: {
    icon: AlertTriangle,
    bg: 'bg-signal-yellow/10',
    text: 'text-signal-yellow',
    border: 'border-signal-yellow/30',
  },
  moderate: {
    icon: Shield,
    bg: 'bg-yellow-500/10',
    text: 'text-yellow-500',
    border: 'border-yellow-500/30',
  },
  low: {
    icon: CheckCircle,
    bg: 'bg-signal-green/10',
    text: 'text-signal-green',
    border: 'border-signal-green/30',
  },
};

export default function BreachTable({ breaches }: BreachTableProps) {
  if (breaches.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Shield size={48} className="mx-auto mb-4 text-signal-green" />
        <p className="text-lg font-medium text-signal-green">No breaches found</p>
        <p className="text-sm mt-1">This domain appears to be clean</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-teal/20">
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
              Email
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
              Severity
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
              Exposure Type
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
              Sources
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {breaches.map((breach, index) => {
            const config = severityConfig[breach.severity];
            const Icon = config.icon;

            return (
              <tr
                key={`${breach.email}-${index}`}
                className="border-b border-teal/10 hover:bg-teal/5 transition-colors"
              >
                <td className="py-3 px-4 font-mono text-sm">
                  {breach.emailMasked}
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}
                  >
                    <Icon size={12} />
                    {breach.severity.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-gray-300">
                  {getExposureType(breach)}
                </td>
                <td className="py-3 px-4 text-sm text-gray-400">
                  {breach.sources.slice(0, 2).join(', ')}
                  {breach.sources.length > 2 && '...'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-400">
                  {breach.breachDate || 'Unknown'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getExposureType(breach: ProcessedBreach): string {
  const types: string[] = [];
  if (breach.hasPlaintextPassword) types.push('Plaintext');
  if (breach.hasPasswordHash) types.push('Hash');
  if (breach.isStealerLog) types.push('Malware');
  return types.length > 0 ? types.join(', ') : 'Email Only';
}
